import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveState = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions {
  /** Debounce in ms after the last change before a save fires. Default 800. */
  delay?: number;
  /**
   * When false the hook is fully inert - no debounce, no flush, no snapshot
   * seeding. Flip it true once the screen has loaded the real value it edits
   * (an async draft load, an access check) so the first save compares against
   * that value, not a placeholder.
   */
  enabled?: boolean;
}

export interface UseAutoSaveResult {
  status: AutoSaveState;
  error: Error | null;
  /** Cancel the pending debounce and save right now (blur handler). */
  flush: () => void;
  /** Re-attempt after an error. */
  retry: () => void;
  isDirty: boolean;
}

const serialize = (v: unknown): string => {
  try {
    return JSON.stringify(v) ?? '';
  } catch {
    return String(v);
  }
};

/**
 * Debounced auto-save. Watches `value`; when it differs from the last
 * persisted snapshot, waits `delay` ms of quiet then calls `saveFn`. Also
 * flushes on `flush()` (wire to the form's `onBlur`), on tab close, and on
 * unmount. Never runs two `saveFn` calls concurrently - a change that lands
 * mid-save is coalesced into exactly one follow-up.
 */
export function useAutoSave<T>(
  value: T,
  saveFn: (value: T) => unknown | Promise<unknown>,
  options: UseAutoSaveOptions = {},
): UseAutoSaveResult {
  const { delay = 800, enabled = true } = options;

  const [status, setStatus] = useState<AutoSaveState>('idle');
  const [error, setError] = useState<Error | null>(null);

  const valueRef = useRef(value);
  valueRef.current = value;
  const saveRef = useRef(saveFn);
  saveRef.current = saveFn;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // JSON of the last successfully persisted value. null until the hook is
  // first enabled, at which point it's seeded from the current value so the
  // mount / post-load value never triggers a save.
  const savedSnapshotRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);

  const runSave = useCallback(async () => {
    if (!enabledRef.current) return;
    const snap = serialize(valueRef.current);
    if (snap === savedSnapshotRef.current) return;
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    inFlightRef.current = true;
    if (mountedRef.current) {
      setStatus('saving');
      setError(null);
    }
    try {
      await saveRef.current(valueRef.current);
      savedSnapshotRef.current = snap;
      if (mountedRef.current) setStatus('saved');
    } catch (e) {
      // Snapshot left unadvanced so the next change / retry() tries again.
      if (mountedRef.current) {
        setError(e instanceof Error ? e : new Error(String(e)));
        setStatus('error');
      }
    } finally {
      inFlightRef.current = false;
      if (pendingRef.current) {
        pendingRef.current = false;
        void runSave();
      }
    }
  }, []);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    void runSave();
  }, [runSave]);

  const retry = useCallback(() => {
    void runSave();
  }, [runSave]);

  // Seed the "already persisted" snapshot the first render the hook is
  // enabled. Idempotent (null guard) so StrictMode's double-invoke is safe.
  useEffect(() => {
    if (enabled && savedSnapshotRef.current === null) {
      savedSnapshotRef.current = serialize(valueRef.current);
    }
  }, [enabled]);

  // Debounce on value change.
  useEffect(() => {
    if (!enabled || savedSnapshotRef.current === null) return;
    if (serialize(value) === savedSnapshotRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void runSave();
    }, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, enabled, delay, runSave]);

  // Best-effort flush on tab close + on unmount. Can't await here, so this is
  // fire-and-forget; the debounce + onBlur flush cover the common path.
  useEffect(() => {
    const flushNow = () => {
      if (
        enabledRef.current &&
        !inFlightRef.current &&
        serialize(valueRef.current) !== savedSnapshotRef.current
      ) {
        void saveRef.current(valueRef.current);
      }
    };
    window.addEventListener('beforeunload', flushNow);
    return () => {
      window.removeEventListener('beforeunload', flushNow);
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      flushNow();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    error,
    flush,
    retry,
    isDirty: savedSnapshotRef.current !== null && serialize(value) !== savedSnapshotRef.current,
  };
}
